/**
 * MobileAppointmentsAgenda — phone / tablet ( <lg ) presentation of the
 * Appointments (Calendar) page. Replaces the desktop month grid with a
 * Today / Upcoming vertical agenda. Batch 5.2 — read-only rendering of
 * existing data; every mutation (New / Edit / Cancel / Delete / Complete
 * / Handover PDF) is handled by the SAME drawer + dialog the desktop
 * uses. No new mutation surface, no new API, no new status.
 *
 * Attention note (per reviewer): the "Needs attention" chip is shown ONLY
 * when `a.status === "needs_attention"`. Service-user care/safety flags
 * are displayed separately via the existing `CareFlagsPills` component
 * so appointment workflow status and clinical safety information stay
 * semantically distinct.
 */
import React, { useMemo } from "react";
import {
  ChevronLeft, ChevronRight, CalendarDays, Plus, Filter, MoreVertical,
  RefreshCw, UserRound, MapPin, Clock, Stethoscope, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CareFlagsPills from "@/components/CareFlagsPills";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function safeClientName(a) {
  const n = (a?.client_name || "").trim();
  if (!n || UUID_RE.test(n)) return "Unknown Service User";
  return n;
}
function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); }
  catch { return (iso || "").slice(11, 16); }
}
function endTime(iso, mins) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (mins) d.setMinutes(d.getMinutes() + Number(mins));
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
function pad2(n) { return String(n).padStart(2, "0"); }
function toLocalYMD(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function dayHeaderLabel(dayIso) {
  const now = new Date();
  const today = toLocalYMD(now);
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowIso = toLocalYMD(tomorrow);
  const d = new Date(dayIso + "T00:00:00");
  if (dayIso === today) return `Today · ${d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}`;
  if (dayIso === tomorrowIso) return `Tomorrow · ${d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}`;
  return d.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "short" });
}

// -----------------------------------------------------------------------------
// Appointment card — whole card opens the existing drawer on tap.
// Priority order per reviewer: Service User → Appointment Type → Date/Time →
// Location → Escort → Status → Safety flags → clinician / notes.
// -----------------------------------------------------------------------------
function AppointmentCard({ a, safety, typeMeta, statusMeta, onOpenDrawer }) {
  const t = typeMeta(a.appointment_type);
  const s = statusMeta(a.status);
  const TIcon = t.Icon; const SIcon = s.Icon;
  const flags = safety?.[a.client_id]?.care_flags || [];
  const isCancelled = a.status === "cancelled";
  const isNeedsAttention = a.status === "needs_attention";

  return (
    <button
      type="button"
      onClick={() => onOpenDrawer(a)}
      data-testid={`mobile-appt-card-${a.id}`}
      className={`w-full text-left rounded-lg border-l-4 ${t.border} border-y border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 min-h-[44px] active:bg-slate-50 dark:active:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500 ${isCancelled ? "opacity-60" : ""}`}
    >
      {/* Row 1 · Service user (bold) + Needs-attention (status-only) */}
      <div className="flex items-center gap-2 mb-1">
        <div
          className={`font-semibold text-slate-900 dark:text-slate-100 truncate flex-1 ${isCancelled ? "line-through" : ""}`}
          data-testid={`mobile-appt-client-${a.id}`}
        >
          {safeClientName(a)}
        </div>
        {isNeedsAttention && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-md border bg-amber-100 text-amber-900 border-amber-400 shrink-0"
            data-testid={`mobile-appt-attention-${a.id}`}
          >
            <AlertTriangle className="w-3 h-3" /> Needs attention
          </span>
        )}
      </div>

      {/* Row 2 · Appointment type chip + title */}
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 rounded ${t.soft} ${t.text} ${t.border}`}>
          <TIcon className="w-3 h-3" /> {t.label}
        </span>
        {a.title && (
          <span className="text-xs text-slate-700 dark:text-slate-300 truncate">{a.title}</span>
        )}
      </div>

      {/* Row 3 · Date/Time */}
      <div className="text-xs text-slate-700 dark:text-slate-300 flex items-center gap-1 tabular-nums">
        <Clock className="w-3 h-3 text-slate-400 shrink-0" />
        <span className="font-medium">{fmtTime(a.scheduled_at)}</span>
        {a.duration_min ? <span className="opacity-80">– {endTime(a.scheduled_at, a.duration_min)}</span> : null}
        {a.duration_min ? <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-1">({a.duration_min}m)</span> : null}
      </div>

      {/* Row 4 · Location */}
      {a.location && (
        <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 inline-flex items-center gap-1 truncate max-w-full" data-testid={`mobile-appt-location-${a.id}`}>
          <MapPin className="w-3 h-3 shrink-0" /> {a.location}
        </div>
      )}

      {/* Row 5 · Escort — amber row when set */}
      {a.escort_staff && (
        <div
          className="mt-1 text-[11px] inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 px-1.5 py-0.5 text-amber-900 dark:text-amber-200"
          data-testid={`mobile-appt-escort-${a.id}`}
        >
          <UserRound className="w-3 h-3" /> <span className="font-semibold uppercase tracking-widest text-[9px]">Escort</span>
          <span className="ml-1">{a.escort_staff}</span>
        </div>
      )}

      {/* Row 6 · Appointment status */}
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        <span
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 rounded ${s.pill}`}
          data-testid={`mobile-appt-status-${a.id}`}
        >
          <SIcon className="w-3 h-3" /> {s.label}
        </span>
        {/* Row 7 · Safety flags — separate from appointment status */}
        {flags.length > 0 && (
          <span data-testid={`mobile-appt-safety-${a.id}`}>
            <CareFlagsPills flags={flags} size="sm" />
          </span>
        )}
      </div>

      {/* Row 8 · Clinician + notes preview */}
      {a.clinician && (
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 inline-flex items-center gap-1 truncate">
          <Stethoscope className="w-3 h-3" /> {a.clinician}
        </div>
      )}
      {a.notes && (
        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 line-clamp-2 whitespace-pre-wrap">
          {a.notes}
        </div>
      )}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Main component. Renders a Today / Upcoming vertical agenda grouped by day
// using the same filtered `items` and `safety` the desktop calendar uses.
// -----------------------------------------------------------------------------
export default function MobileAppointmentsAgenda({
  loading, items, filtered, summary, safety, typeMeta, statusMeta,
  monthLabel, filterCount, filterOnlyToday, filterOnlyEscort, filterStatus,
  filterType, onShift, onGoToday, onOpenAdd, onOpenDrawer, onOpenFilters,
  onRefresh, onClearFilters, onToggleToday, onToggleEscort, onToggleHospital,
  onToggleAttn,
}) {
  // Group current filtered items by ISO day; sorted ascending. This mirrors
  // the desktop `byDay` build but scoped to the mobile agenda's ordering.
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((a) => {
      const day = (a.scheduled_at || "").slice(0, 10);
      if (!day) return;
      (map[day] ||= []).push(a);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((x, y) => (x.scheduled_at || "").localeCompare(y.scheduled_at || "")),
    );
    return map;
  }, [filtered]);
  const dayKeys = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  return (
    <div
      className="lg:hidden flex-1 min-h-0 overflow-auto bg-slate-50 dark:bg-slate-950"
      data-testid="mobile-appointments"
    >
      <div className="px-3 pt-3 pb-6 space-y-3 max-w-full">
        {/* Section title + primary New button */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Care Delivery</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight" data-testid="mobile-appointments-title">
              Appointments
            </h2>
          </div>
          <Button
            onClick={onOpenAdd}
            data-testid="mobile-appointments-new"
            className="h-11 bg-slate-900 hover:bg-slate-800 text-white shrink-0"
          >
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>

        {/* Month navigator */}
        <div className="flex items-center gap-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
          <button
            type="button"
            onClick={() => onShift(-1)}
            data-testid="mobile-appt-prev-month"
            aria-label="Previous month"
            className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onGoToday}
            data-testid="mobile-appt-today"
            className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 h-11 rounded-md text-sm font-semibold text-slate-800 dark:text-slate-100"
          >
            <CalendarDays className="w-4 h-4 shrink-0" />
            <span className="truncate">{monthLabel}</span>
          </button>
          <button
            type="button"
            onClick={() => onShift(1)}
            data-testid="mobile-appt-next-month"
            aria-label="Next month"
            className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Filters + More */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onOpenFilters}
            data-testid="mobile-appt-filters"
            className="h-11 flex-1"
          >
            <Filter className="w-4 h-4 mr-1" /> Filters
            {filterCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-800">{filterCount}</span>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                data-testid="mobile-appt-more"
                className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 shrink-0"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Actions</DropdownMenuLabel>
              <DropdownMenuItem onSelect={onRefresh} data-testid="mobile-appt-refresh">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* 5 summary tiles — 2-up grid */}
        <div className="grid grid-cols-2 gap-2" data-testid="mobile-appt-summary">
          <SummaryTile label="Appointments" value={summary.total} active={false} onClick={onClearFilters} tone="slate" testid="mobile-summary-total" />
          <SummaryTile label="Today" value={summary.today} active={filterOnlyToday} onClick={onToggleToday} tone="sky" testid="mobile-summary-today" />
          <SummaryTile label="Hospital" value={summary.hospital} active={filterType === "Hospital"} onClick={onToggleHospital} tone="purple" testid="mobile-summary-hospital" />
          <SummaryTile label="Escort required" value={summary.escort} active={filterOnlyEscort} onClick={onToggleEscort} tone="amber" testid="mobile-summary-escort" />
          <SummaryTile label="Needs attention" value={summary.attn} active={filterStatus === "no_show"} onClick={onToggleAttn} tone="red" testid="mobile-summary-attn" />
        </div>

        {/* Agenda */}
        {loading ? (
          <div className="text-sm text-slate-500 p-4" data-testid="mobile-appt-loading">Loading month…</div>
        ) : dayKeys.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center" data-testid="mobile-appt-empty">
            <CalendarDays className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">No appointments in this month</div>
            <div className="text-xs text-slate-500 mt-1">
              {filterCount > 0
                ? <>Try clearing your filters.</>
                : <>Use the navigator above to jump to another month.</>}
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="mobile-appt-agenda">
            {dayKeys.map((day) => (
              <section key={day} data-testid={`mobile-appt-day-${day}`}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    {dayHeaderLabel(day)}
                  </div>
                  <div className="text-[10px] text-slate-400 tabular-nums">· {grouped[day].length} appt{grouped[day].length === 1 ? "" : "s"}</div>
                </div>
                <div className="space-y-2">
                  {grouped[day].map((a) => (
                    <AppointmentCard
                      key={a.id}
                      a={a}
                      safety={safety}
                      typeMeta={typeMeta}
                      statusMeta={statusMeta}
                      onOpenDrawer={onOpenDrawer}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, active, onClick, tone, testid }) {
  const tones = {
    slate:  "bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100",
    sky:    "bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200",
    purple: "bg-purple-50 text-purple-900 dark:bg-purple-950/40 dark:text-purple-200",
    amber:  "bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
    red:    "bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`min-h-[44px] text-left rounded-lg border p-3 ${tones[tone]} ${active ? "ring-2 ring-sky-500 border-sky-400" : "border-slate-200 dark:border-slate-800"}`}
    >
      <div className="text-[9px] uppercase tracking-widest opacity-70 font-semibold truncate">{label}</div>
      <div className="text-xl font-heading font-semibold tabular-nums mt-0.5 leading-tight">{value}</div>
    </button>
  );
}
